import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Trash2, Plus, Lock } from "lucide-react";

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [donations, setDonations] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [interests, setInterests] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [members, setMembers] = useState([]);
  const [stories, setStories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [goals, setGoals] = useState(null);
  const [goalsForm, setGoalsForm] = useState({});

  // Forms
  const [newStory, setNewStory] = useState({ title: "", author: "", location: "", excerpt: "", image_url: "" });
  const [newGallery, setNewGallery] = useState({ caption: "", image_url: "", tag: "" });

  const loadAll = async () => {
    try {
      const [ov, dn, vl, ct, di, pa, me, st, gl, go] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/donations"),
        api.get("/admin/volunteers"),
        api.get("/admin/contacts"),
        api.get("/admin/donation-interests"),
        api.get("/admin/partnerships"),
        api.get("/admin/members"),
        api.get("/admin/stories"),
        api.get("/gallery"),
        api.get("/goals"),
      ]);
      setOverview(ov.data);
      setDonations(dn.data);
      setVolunteers(vl.data);
      setContacts(ct.data);
      setInterests(di.data);
      setPartnerships(pa.data);
      setMembers(me.data);
      setStories(st.data);
      setGallery(gl.data);
      setGoals(go.data);
      setGoalsForm(go.data);
    } catch (e) {
      // unauthorized handled below
    }
  };

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[var(--bone)]"><div className="overline text-[var(--terracotta)]">Loading…</div></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[var(--bone)]">
        <Nav />
        <div className="container-x pt-40 max-w-md">
          <div className="overline text-[var(--terracotta)]">Admin</div>
          <h1 className="font-serif text-5xl mt-4 tracking-[-0.025em]">Sign in required</h1>
          <p className="text-[var(--ink)]/70 mt-4">This dashboard is restricted to foundation staff.</p>
          <button data-testid="admin-login-btn" onClick={login} className="btn-primary mt-8"><Lock className="w-4 h-4" /> Sign in with Google</button>
        </div>
      </div>
    );
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[var(--bone)]">
        <Nav />
        <div className="container-x pt-40 max-w-md">
          <div className="overline text-[var(--terracotta)]">403</div>
          <h1 className="font-serif text-5xl mt-4 tracking-[-0.025em]">Not authorised</h1>
          <p className="text-[var(--ink)]/70 mt-4">Your account doesn&apos;t have admin permissions. Contact a foundation administrator.</p>
        </div>
      </div>
    );
  }

  const createStory = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/stories", newStory);
      toast.success("Story published");
      setNewStory({ title: "", author: "", location: "", excerpt: "", image_url: "" });
      loadAll();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteStory = async (id) => {
    await api.delete(`/admin/stories/${id}`);
    toast.success("Story removed");
    loadAll();
  };

  const createGallery = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/gallery", newGallery);
      toast.success("Image added");
      setNewGallery({ caption: "", image_url: "", tag: "" });
      loadAll();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteGallery = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Removed");
    loadAll();
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "goals", label: "Goals" },
    { id: "members", label: "Members" },
    { id: "interests", label: "Donor sign-ups" },
    { id: "partnerships", label: "Partnerships" },
    { id: "donations", label: "Donations" },
    { id: "volunteers", label: "Volunteers" },
    { id: "contacts", label: "Contacts" },
    { id: "stories", label: "Stories" },
    { id: "gallery", label: "Gallery" },
  ];

  const saveGoals = async (e) => {
    e.preventDefault();
    const payload = {
      schools_target: Number(goalsForm.schools_target),
      schools_current: Number(goalsForm.schools_current),
      students_target: Number(goalsForm.students_target),
      students_current: Number(goalsForm.students_current),
      deadline_year: Number(goalsForm.deadline_year),
      note: goalsForm.note,
    };
    try {
      const res = await api.put("/admin/goals", payload);
      setGoals(res.data);
      setGoalsForm(res.data);
      toast.success("Goals updated");
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[var(--bone)] text-[var(--ink)]">
      <Nav />
      <div className="pt-28 pb-24 container-x">
        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="col-span-12 md:col-span-3">
            <div className="overline text-[var(--terracotta)]">Admin</div>
            <div className="font-serif text-3xl mt-2 leading-tight">Control room</div>
            <div className="text-xs text-[var(--muted)] mt-2 font-mono">{user.email}</div>
            <nav className="mt-8 flex md:flex-col gap-1 flex-wrap">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`text-left px-4 py-3 border text-sm transition-colors ${tab === t.id ? "bg-[var(--forest)] text-[var(--bone)] border-[var(--forest)]" : "border-black/10 hover:border-[var(--ink)]"}`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <main className="col-span-12 md:col-span-9">
            {tab === "overview" && overview && (
              <div data-testid="admin-overview" className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
                {[
                  { l: "Members", v: overview.members_count ?? 0 },
                  { l: "Donation sign-ups", v: overview.donation_interests_count },
                  { l: "Partnership leads", v: overview.partnerships_count },
                  { l: "Total raised (paid)", v: `$${overview.total_donations_usd}` },
                  { l: "Donations enabled", v: overview.donations_enabled ? "Yes" : "Not yet" },
                  { l: "Volunteers", v: overview.volunteers_count },
                  { l: "Contact messages", v: overview.contact_count },
                  { l: "Stories published", v: overview.stories_count },
                ].map((s) => (
                  <div key={s.l} className="bg-[var(--bone)] p-6">
                    <div className="overline text-[var(--muted)]">{s.l}</div>
                    <div className="font-serif text-4xl mt-2 tracking-[-0.02em]">{s.v}</div>
                  </div>
                ))}
              </div>
            )}

            {tab === "interests" && (
              <table data-testid="admin-interests-table" className="w-full text-sm border border-black/10">
                <thead className="bg-[var(--bone-2)] text-left">
                  <tr><th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Note</th></tr>
                </thead>
                <tbody>
                  {interests.map((i) => (
                    <tr key={i.id} className="border-t border-black/10">
                      <td className="p-3 font-mono text-xs">{(i.created_at || "").slice(0, 16)}</td>
                      <td className="p-3">{i.name || "—"}</td>
                      <td className="p-3">{i.email}</td>
                      <td className="p-3">{i.note || ""}</td>
                    </tr>
                  ))}
                  {interests.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-[var(--muted)]">No sign-ups yet.</td></tr>}
                </tbody>
              </table>
            )}

            {tab === "partnerships" && (
              <div data-testid="admin-partnerships-list" className="space-y-3">
                {partnerships.length === 0 && <div className="p-6 text-center text-[var(--muted)] border border-black/10">No partnership requests yet.</div>}
                {partnerships.map((p) => (
                  <div key={p.id} className="border border-black/10 p-5">
                    <div className="flex justify-between text-xs font-mono text-[var(--muted)]">
                      <span>{(p.created_at || "").slice(0, 16)} · <span className="uppercase tracking-wider">{p.type}</span></span>
                      <span>{p.email}{p.website ? ` · ${p.website}` : ""}</span>
                    </div>
                    <div className="font-serif text-xl mt-2">{p.organization}</div>
                    <div className="text-xs text-[var(--muted)]">Contact: {p.contact_name}</div>
                    <p className="text-sm text-[var(--ink)]/80 mt-3">{p.message}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === "members" && (
              <div data-testid="admin-members-list">
                <table className="w-full text-sm border border-black/10">
                  <thead className="bg-[var(--bone-2)] text-left">
                    <tr><th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Pledged</th><th className="p-3">Status</th><th className="p-3">Note</th></tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.id} className="border-t border-black/10 align-top">
                        <td className="p-3 font-mono text-xs">{(m.created_at || "").slice(0, 16)}</td>
                        <td className="p-3">{m.name}</td>
                        <td className="p-3 break-all">{m.email}</td>
                        <td className="p-3">{m.phone || "—"}</td>
                        <td className="p-3 font-mono">${m.pledged_amount}</td>
                        <td className="p-3"><span className={`px-2 py-1 text-xs ${m.status === "paid" ? "bg-[var(--terracotta)] text-[var(--bone)]" : "bg-black/10"}`}>{m.status}</span></td>
                        <td className="p-3 text-xs text-[var(--ink)]/75 max-w-xs">{m.motivation || ""}</td>
                      </tr>
                    ))}
                    {members.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-[var(--muted)]">No members yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "goals" && goals && (
              <form onSubmit={saveGoals} data-testid="admin-goals-form" className="border border-black/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-[var(--bone-2)]">
                <div className="md:col-span-2 overline text-[var(--terracotta)]">Update progress towards our {goals.deadline_year} goal</div>
                <label className="block">
                  <span className="overline text-[var(--muted)]">Schools target</span>
                  <input type="number" className="editorial-input mt-2" value={goalsForm.schools_target ?? ""} onChange={(e) => setGoalsForm({ ...goalsForm, schools_target: e.target.value })} />
                </label>
                <label className="block">
                  <span className="overline text-[var(--muted)]">Schools rebuilt so far</span>
                  <input type="number" className="editorial-input mt-2" value={goalsForm.schools_current ?? ""} onChange={(e) => setGoalsForm({ ...goalsForm, schools_current: e.target.value })} />
                </label>
                <label className="block">
                  <span className="overline text-[var(--muted)]">Students target</span>
                  <input type="number" className="editorial-input mt-2" value={goalsForm.students_target ?? ""} onChange={(e) => setGoalsForm({ ...goalsForm, students_target: e.target.value })} />
                </label>
                <label className="block">
                  <span className="overline text-[var(--muted)]">Students reached so far</span>
                  <input type="number" className="editorial-input mt-2" value={goalsForm.students_current ?? ""} onChange={(e) => setGoalsForm({ ...goalsForm, students_current: e.target.value })} />
                </label>
                <label className="block">
                  <span className="overline text-[var(--muted)]">Deadline year</span>
                  <input type="number" className="editorial-input mt-2" value={goalsForm.deadline_year ?? ""} onChange={(e) => setGoalsForm({ ...goalsForm, deadline_year: e.target.value })} />
                </label>
                <label className="block md:col-span-2">
                  <span className="overline text-[var(--muted)]">Public note (shown on goals section)</span>
                  <textarea rows={2} className="editorial-input mt-2 resize-none" value={goalsForm.note ?? ""} onChange={(e) => setGoalsForm({ ...goalsForm, note: e.target.value })} />
                </label>
                <button data-testid="admin-save-goals" className="btn-primary md:col-span-2">Save goals</button>
              </form>
            )}

            {tab === "donations" && (
              <table data-testid="admin-donations-table" className="w-full text-sm border border-black/10">
                <thead className="bg-[var(--bone-2)] text-left">
                  <tr><th className="p-3">Date</th><th className="p-3">Donor</th><th className="p-3">Package</th><th className="p-3">Amount</th><th className="p-3">Status</th></tr>
                </thead>
                <tbody>
                  {donations.map((d) => (
                    <tr key={d.session_id} className="border-t border-black/10">
                      <td className="p-3 font-mono text-xs">{(d.created_at || "").slice(0, 16)}</td>
                      <td className="p-3">{d.donor_name || d.donor_email || "Anonymous"}</td>
                      <td className="p-3">{d.package_label}</td>
                      <td className="p-3 font-mono">${d.amount}</td>
                      <td className="p-3"><span className={`px-2 py-1 text-xs ${d.payment_status === "paid" ? "bg-[var(--terracotta)] text-[var(--bone)]" : "bg-black/10"}`}>{d.payment_status}</span></td>
                    </tr>
                  ))}
                  {donations.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-[var(--muted)]">No donations yet.</td></tr>}
                </tbody>
              </table>
            )}

            {tab === "volunteers" && (
              <table data-testid="admin-volunteers-table" className="w-full text-sm border border-black/10">
                <thead className="bg-[var(--bone-2)] text-left">
                  <tr><th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Interest</th><th className="p-3">Skills</th></tr>
                </thead>
                <tbody>
                  {volunteers.map((v) => (
                    <tr key={v.id} className="border-t border-black/10">
                      <td className="p-3 font-mono text-xs">{(v.created_at || "").slice(0, 16)}</td>
                      <td className="p-3">{v.name}</td>
                      <td className="p-3">{v.email}</td>
                      <td className="p-3">{v.interest}</td>
                      <td className="p-3">{v.skills}</td>
                    </tr>
                  ))}
                  {volunteers.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-[var(--muted)]">No volunteers yet.</td></tr>}
                </tbody>
              </table>
            )}

            {tab === "contacts" && (
              <div data-testid="admin-contacts-list" className="space-y-3">
                {contacts.length === 0 && <div className="p-6 text-center text-[var(--muted)] border border-black/10">No messages yet.</div>}
                {contacts.map((c) => (
                  <div key={c.id} className="border border-black/10 p-5">
                    <div className="flex justify-between text-xs font-mono text-[var(--muted)]">
                      <span>{c.name} · {c.email}</span>
                      <span>{(c.created_at || "").slice(0, 16)}</span>
                    </div>
                    <div className="font-serif text-xl mt-2">{c.subject || "(no subject)"}</div>
                    <p className="text-sm text-[var(--ink)]/80 mt-2">{c.message}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === "stories" && (
              <div>
                <form onSubmit={createStory} data-testid="admin-new-story-form" className="border border-black/10 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input className="editorial-input" placeholder="Title" value={newStory.title} onChange={(e) => setNewStory({ ...newStory, title: e.target.value })} required />
                  <input className="editorial-input" placeholder="Author" value={newStory.author} onChange={(e) => setNewStory({ ...newStory, author: e.target.value })} required />
                  <input className="editorial-input" placeholder="Location" value={newStory.location} onChange={(e) => setNewStory({ ...newStory, location: e.target.value })} required />
                  <input className="editorial-input" placeholder="Image URL" value={newStory.image_url} onChange={(e) => setNewStory({ ...newStory, image_url: e.target.value })} required />
                  <textarea className="editorial-input md:col-span-2" rows={2} placeholder="Excerpt" value={newStory.excerpt} onChange={(e) => setNewStory({ ...newStory, excerpt: e.target.value })} required />
                  <button data-testid="admin-create-story-btn" className="btn-primary md:col-span-2"><Plus className="w-4 h-4" /> Publish story</button>
                </form>
                <div className="space-y-3">
                  {stories.map((s) => (
                    <div key={s.id} className="border border-black/10 p-4 flex items-center gap-4">
                      <img src={s.image_url} className="w-20 h-20 object-cover" alt="" />
                      <div className="flex-1">
                        <div className="overline text-[var(--terracotta)]">{s.location}</div>
                        <div className="font-serif text-lg">{s.title}</div>
                        <div className="text-xs text-[var(--muted)]">{s.author}</div>
                      </div>
                      <button data-testid={`admin-delete-story-${s.id}`} onClick={() => deleteStory(s.id)} className="text-red-700 hover:underline"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === "gallery" && (
              <div>
                <form onSubmit={createGallery} data-testid="admin-new-gallery-form" className="border border-black/10 p-6 mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input className="editorial-input" placeholder="Caption" value={newGallery.caption} onChange={(e) => setNewGallery({ ...newGallery, caption: e.target.value })} required />
                  <input className="editorial-input" placeholder="Image URL" value={newGallery.image_url} onChange={(e) => setNewGallery({ ...newGallery, image_url: e.target.value })} required />
                  <input className="editorial-input" placeholder="Tag (rebuild/stem/…)" value={newGallery.tag} onChange={(e) => setNewGallery({ ...newGallery, tag: e.target.value })} />
                  <button data-testid="admin-create-gallery-btn" className="btn-primary md:col-span-3"><Plus className="w-4 h-4" /> Add image</button>
                </form>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {gallery.map((g) => (
                    <div key={g.id} className="relative group">
                      <img src={g.image_url} className="w-full h-40 object-cover" alt="" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center">
                        <button data-testid={`admin-delete-gallery-${g.id}`} onClick={() => deleteGallery(g.id)} className="opacity-0 group-hover:opacity-100 btn-primary !py-2 !px-3 text-xs"><Trash2 className="w-3 h-3" /> Delete</button>
                      </div>
                      <div className="text-xs mt-1 text-[var(--muted)] truncate">{g.caption}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
